/**
 * RepToInternalComposeModal — a slim composer that lets a reseller rep
 * (or agency manager) send a direct message to the Ready Prospect team.
 * Backed by POST /api/direct-threads with recipient_type="internal".
 *
 * Rendered on the Direct Messages page so reps have a visible entry point
 * (prior to this, direct messages could only be started from Agency/Rep
 * detail pages, which reps don't have access to).
 */
import { useRef, useState } from "react";
import { RichTextarea } from "./RichTextarea";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { createDirectThread, uploadFile } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Loader2, MessageSquarePlus, Send, Paperclip, X as XIcon } from "lucide-react";

export default function RepToInternalComposeModal({ trigger = null }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);  // [{id, filename}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const canSubmit = subject.trim() && (body.trim() || attachments.length > 0);

  const handleAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 25 MB — skipped`);
          continue;
        }
        const { data } = await uploadFile(file);
        setAttachments((prev) => [...prev, { id: data.id, filename: data.filename }]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((f) => f.id !== id));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await createDirectThread({
        subject: subject.trim(),
        body: body.trim(),
        recipient_type: "internal",
        file_ids: attachments.map((a) => a.id),
      });
      toast.success("Message sent to Ready Prospect");
      setOpen(false);
      setSubject("");
      setBody("");
      setAttachments([]);
      if (data?.id) navigate(`/direct/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="rep-direct-msg-trigger-btn">
            <MessageSquarePlus className="mr-2 h-4 w-4" />
            New message
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="rep-direct-msg-modal">
        <DialogHeader>
          <DialogTitle>Message the Ready Prospect team</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div
            className="rounded-md border bg-emerald-50 border-emerald-200 px-3 py-2 text-xs text-emerald-800"
            data-testid="rep-direct-msg-recipient-hint"
          >
            Sending to the Ready Prospect team. Someone from our team will reply as soon as possible.
          </div>

          <div className="space-y-2">
            <Label htmlFor="rep-direct-msg-subject">Subject</Label>
            <Input
              id="rep-direct-msg-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Quick question about a client / commission / product"
              data-testid="rep-direct-msg-subject-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rep-direct-msg-body">Message</Label>
            <RichTextarea
              id="rep-direct-msg-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="Type your message…"
              data-testid="rep-direct-msg-body-input"
            />
          </div>

          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="rep-direct-msg-pending-attachments">
              {attachments.map((f) => (
                <span
                  key={f.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary text-[11px] font-medium border"
                >
                  <Paperclip className="h-3 w-3" />
                  <span className="max-w-[180px] truncate">{f.filename}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(f.id)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${f.filename}`}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleAttach}
            data-testid="rep-direct-msg-file-input"
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || submitting}
            className="mr-auto"
            data-testid="rep-direct-msg-attach-btn"
          >
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />}
            Attach
          </Button>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || submitting} data-testid="rep-direct-msg-send-btn">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
